import StickyTableResponsive from '../../components/common/StickyTableResponsive';
import SortableTh from '../../components/common/SortableTh';
import React, { useCallback, useEffect, useState } from 'react';
import { useTableSort } from '../../hooks/useTableSort';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import { AppFilterBar, AppFilterInput } from '../../components/filters';
import { notify } from '../../components/common/AppToast';
import clientMappingService from '../../services/clientMappingService';
import {
  AppBadge,
  AppButton,
  AppEmptyState,
  AppInput,
  AppPillSwitch,
  AppSpinner,
} from '../../components/ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import ClientPropositionsPanel from './ClientPropositionsPanel';
import '../../styles/kkp-planning-page.scss';
import '../../styles/kkp-planning-view-mode.scss';
import '../../styles/client-mapping-admin.scss';

const TABS = [
  {
    id: 'suggested',
    label: 'À confirmer',
    status: 'suggested',
    icon: 'mdi:help-circle-outline',
    hint: 'Liens proposés à vérifier',
  },
  {
    id: 'validated',
    label: 'Déjà liés',
    status: 'validated',
    icon: 'mdi:link-variant-check',
    hint: 'Correspondances actives',
  },
  {
    id: 'orphans',
    label: 'À rattacher',
    status: null,
    icon: 'mdi:folder-account-outline',
    hint: 'Clients encore sans lien',
  },
];

const METHOD_LABELS = {
  exact_slug: 'Nom identique',
  exact_nom: 'Nom identique',
  fuzzy: 'Nom proche',
  manual: 'À la main',
};

const TABLE_SORT_COLUMNS = {
  drive: { type: 'string', getValue: (row) => row.drive_nom || row.nom_canonique },
  dolibarr: { type: 'string', getValue: (row) => row.dolibarr_nom },
  confidence: { type: 'number', getValue: (row) => row.match_confidence ?? -1 },
  method: { type: 'string', getValue: (row) => METHOD_LABELS[row.match_method] ?? row.match_method },
};

const emptyLinkForm = () => ({
  drive_folder_id: '',
  drive_nom: '',
  dolibarr_thirdparty_id: '',
  dolibarr_nom: '',
});

const ActionButton = ({
  variant = 'muted',
  icon,
  label,
  title,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    className={`client-mapping-action client-mapping-action--${variant}`}
    title={title || label}
    disabled={disabled}
    onClick={onClick}
  >
    <Icon icon={icon} className="client-mapping-action__icon" />
    <span>{label}</span>
  </button>
);

const ClientMappingAdminPage = () => {
  const { ask, modal } = useAppConfirm();
  const [tab, setTab] = useState('suggested');
  const [source, setSource] = useState('drive');
  const [mappings, setMappings] = useState([]);
  const [orphans, setOrphans] = useState({ drive: [], dolibarr: [] });
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [orphanSharedQuery, setOrphanSharedQuery] = useState('');
  const [orphanDriveQuery, setOrphanDriveQuery] = useState('');
  const [orphanDolibarrQuery, setOrphanDolibarrQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [linkForm, setLinkForm] = useState(emptyLinkForm);
  const [correctingId, setCorrectingId] = useState(null);
  const [correctQuery, setCorrectQuery] = useState('');
  const [correctSuggestions, setCorrectSuggestions] = useState([]);
  const [counts, setCounts] = useState({ suggested: 0, validated: 0, orphans: 0 });

  const currentTab = TABS.find((item) => item.id === tab) || TABS[0];
  const { sortedRows, sort, toggleSort, getSortIcon } = useTableSort(mappings, TABLE_SORT_COLUMNS);

  const normalizeSearch = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  const filteredOrphanDrive = orphans.drive.filter((client) => {
    const q = normalizeSearch(orphanDriveQuery);
    if (!q) return true;
    return normalizeSearch(`${client.nom} ${client.slug}`).includes(q);
  });

  const filteredOrphanDolibarr = orphans.dolibarr.filter((tp) => {
    const q = normalizeSearch(orphanDolibarrQuery);
    if (!q) return true;
    return normalizeSearch(`${tp.nom} ${tp.siret || ''}`).includes(q);
  });

  const canConfirmOrphanLink = Boolean(
    linkForm.drive_folder_id && linkForm.dolibarr_thirdparty_id,
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const refreshCounts = useCallback(async () => {
    try {
      const [suggested, validated, orphansData] = await Promise.all([
        clientMappingService.list({ status: 'suggested', limit: 500 }),
        clientMappingService.list({ status: 'validated', limit: 500 }),
        clientMappingService.listOrphans({ limit: 500 }),
      ]);
      setCounts({
        suggested: suggested.length,
        validated: validated.length,
        orphans: (orphansData.drive?.length || 0) + (orphansData.dolibarr?.length || 0),
      });
    } catch {
      // Les compteurs sont indicatifs ; ne pas bloquer l’écran.
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (currentTab.id === 'orphans') {
        const data = await clientMappingService.listOrphans({ limit: 500 });
        setOrphans({
          drive: Array.isArray(data.drive) ? data.drive : [],
          dolibarr: Array.isArray(data.dolibarr) ? data.dolibarr : [],
        });
        setMappings([]);
        setLinkForm(emptyLinkForm());
      } else {
        const rows = await clientMappingService.list({
          status: currentTab.status,
          q: debouncedQuery,
          limit: 300,
        });
        setMappings(rows);
      }
    } catch (err) {
      notify.error(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [currentTab.id, currentTab.status, debouncedQuery]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  const bumpCounts = (patch) => {
    setCounts((prev) => ({
      suggested: Math.max(0, (prev.suggested ?? 0) + (patch.suggested ?? 0)),
      validated: Math.max(0, (prev.validated ?? 0) + (patch.validated ?? 0)),
      orphans: Math.max(0, (prev.orphans ?? 0) + (patch.orphans ?? 0)),
    }));
  };

  const removeMappingRow = (id) => {
    setMappings((prev) => prev.filter((row) => row.id !== id));
  };

  const updateMappingRow = (id, patch) => {
    setMappings((prev) => prev.map((row) => (
      row.id === id ? { ...row, ...patch } : row
    )));
  };

  const runAutoMatch = async () => {
    setBusy(true);
    try {
      const stats = await clientMappingService.autoMatch({ refreshDrive: true });
      const linked = stats.auto_linked ?? 0;
      const toConfirm = stats.suggested ?? 0;
      const unresolved = stats.unresolved ?? 0;
      notify.success(
        `Liaison auto terminée : ${linked} lié(s) automatiquement`
        + (toConfirm ? `, ${toConfirm} à confirmer` : '')
        + (unresolved ? `, ${unresolved} sans correspondance` : '')
        + '.',
      );
      setTab(linked > 0 && toConfirm === 0 ? 'validated' : 'suggested');
      await Promise.all([load(), refreshCounts()]);
    } catch (err) {
      notify.error(err.message || 'Échec de la liaison automatique');
    } finally {
      setBusy(false);
    }
  };

  const handleValidate = async (id) => {
    setBusyId(id);
    try {
      await clientMappingService.validate(id);
      removeMappingRow(id);
      bumpCounts({ suggested: -1, validated: 1 });
      notify.success('Lien confirmé');
    } catch (err) {
      notify.error(err.message || 'Confirmation impossible');
    } finally {
      setBusyId(null);
    }
  };

  const handleIgnore = async (id) => {
    setBusyId(id);
    try {
      await clientMappingService.ignore(id);
      removeMappingRow(id);
      bumpCounts({ suggested: -1 });
      notify.success('Proposition ignorée');
    } catch (err) {
      notify.error(err.message || 'Action impossible');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (id) => {
    const ok = await ask({
      title: 'Retirer ce lien ?',
      message: 'Retirer ce lien entre le dossier et l’entreprise ?',
      confirmLabel: 'Retirer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await clientMappingService.remove(id);
      removeMappingRow(id);
      bumpCounts({ validated: -1, orphans: 2 });
      notify.success('Lien retiré');
    } catch (err) {
      notify.error(err.message || 'Suppression impossible');
    } finally {
      setBusyId(null);
    }
  };

  const searchDolibarr = async (value, setter) => {
    if (value.trim().length < 2) {
      setter([]);
      return;
    }
    try {
      setter(await clientMappingService.searchDolibarr(value));
    } catch {
      setter([]);
    }
  };

  const handleCreateLink = async () => {
    if (!canConfirmOrphanLink) return;
    setBusy(true);
    const driveId = linkForm.drive_folder_id;
    const dolibarrId = linkForm.dolibarr_thirdparty_id;
    try {
      await clientMappingService.create({
        drive_folder_id: driveId,
        drive_nom: linkForm.drive_nom,
        dolibarr_thirdparty_id: Number(dolibarrId),
        validate: true,
      });
      setOrphans((prev) => ({
        drive: prev.drive.filter((c) => c.folder_id !== driveId),
        dolibarr: prev.dolibarr.filter((tp) => String(tp.id) !== String(dolibarrId)),
      }));
      setLinkForm(emptyLinkForm());
      bumpCounts({ orphans: -2, validated: 1 });
      notify.success('Les deux fiches sont maintenant liées');
    } catch (err) {
      notify.error(err.message || 'Création du lien impossible');
    } finally {
      setBusy(false);
    }
  };

  const applyCorrection = async (mappingId, thirdparty) => {
    setBusyId(mappingId);
    try {
      await clientMappingService.update(mappingId, {
        dolibarr_thirdparty_id: thirdparty.id,
        match_status: 'validated',
      });
      setCorrectingId(null);
      setCorrectQuery('');
      setCorrectSuggestions([]);

      if (tab === 'suggested') {
        removeMappingRow(mappingId);
        bumpCounts({ suggested: -1, validated: 1 });
      } else {
        updateMappingRow(mappingId, {
          dolibarr_thirdparty_id: thirdparty.id,
          dolibarr_nom: thirdparty.nom || '',
          match_status: 'validated',
          match_method: 'manual',
          match_confidence: 100,
        });
      }
      notify.success('Lien mis à jour');
    } catch (err) {
      notify.error(err.message || 'Correction impossible');
    } finally {
      setBusyId(null);
    }
  };

  const methodBadgeVariant = (method) => {
    if (method === 'exact_slug' || method === 'exact_nom') return 'success';
    if (method === 'fuzzy') return 'warning';
    return 'neutral';
  };

  const selectDriveOrphan = (client) => {
    setLinkForm((prev) => ({
      ...prev,
      drive_folder_id: client.folder_id,
      drive_nom: client.nom,
    }));
  };

  const selectDolibarrOrphan = (tp) => {
    setLinkForm((prev) => ({
      ...prev,
      dolibarr_thirdparty_id: String(tp.id),
      dolibarr_nom: tp.nom || '',
    }));
  };

  const setOrphanSharedSearch = (value) => {
    setOrphanSharedQuery(value);
    setOrphanDriveQuery(value);
    setOrphanDolibarrQuery(value);
  };

  return (
    <MasterLayout>
      <Breadcrumb
        title="Correspondances clients"
        subtitle="Relier Dolibarr à Drive, HubSpot et au RAG"
        actions={source === 'drive' ? (
          <AppButton
            size="sm"
            icon="mdi:magic-staff"
            loading={busy}
            onClick={runAutoMatch}
          >
            Lier automatiquement
          </AppButton>
        ) : null}
      />

      <div className="card kkp-planning-results-card">
        <div className="kkp-planning-view-toolbar justify-content-start">
          <AppPillSwitch
            ariaLabel="Source de correspondance"
            value={source}
            onChange={setSource}
            options={[
              { value: 'drive', label: 'Drive', icon: 'mdi:folder-outline', hint: 'Dossiers clients' },
              { value: 'hubspot', label: 'HubSpot', icon: 'mdi:hubspot', hint: 'Companies' },
              { value: 'rag', label: 'RAG', icon: 'mdi:book-search-outline', hint: 'Slugs documentaires' },
            ]}
          />
        </div>
        {source !== 'drive' ? (
          <div className="card-body">
            <ClientPropositionsPanel source={source} />
          </div>
        ) : (
        <>
        <div className="kkp-planning-view-toolbar justify-content-start">
          <AppPillSwitch
            ariaLabel="Vues correspondances clients"
            value={tab}
            onChange={(next) => {
              setTab(next);
              setCorrectingId(null);
              if (next !== 'orphans') {
                setLinkForm(emptyLinkForm());
              } else {
                setOrphanSharedSearch('');
              }
            }}
            options={TABS.map((item) => ({
              value: item.id,
              label: item.label,
              icon: item.icon,
              hint: item.hint,
              count: counts[item.id] ?? 0,
            }))}
          />
        </div>

        <div className="card-body">
          {tab !== 'orphans' && (
            <AppFilterBar
              className="mb-3 shadow-none border"
              onReset={() => setQuery('')}
              resetDisabled={!query}
            >
              <AppFilterInput
                id="client-mapping-search"
                label="Rechercher un client"
                col="col-12 col-md-8 col-lg-6"
                placeholder="Nom du dossier ou de l’entreprise…"
                value={query}
                onChange={setQuery}
              />
            </AppFilterBar>
          )}

          {tab === 'orphans' ? (
            <div className="row g-3">
              <div className="col-12">
                <div className="client-mapping-orphans-intro">
                  <div className="client-mapping-orphans-intro__icon" aria-hidden>
                    <Icon icon="mdi:gesture-tap" />
                  </div>
                  <div>
                    <p className="client-mapping-orphans-intro__title">Rattacher un client en 2 clics</p>
                    <p className="client-mapping-orphans-intro__text">
                      Choisissez un dossier à gauche, une entreprise à droite, puis confirmez le lien
                      en bas de page. Aucune connaissance technique n’est nécessaire.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="client-mapping-orphan-shared-search position-relative">
                  <Icon
                    icon="mdi:magnify"
                    className="position-absolute top-50 translate-middle-y text-secondary-light"
                    style={{ left: 14, pointerEvents: 'none' }}
                  />
                  <AppInput
                    type="search"
                    className="ps-40"
                    placeholder="Rechercher dans Drive et Dolibarr…"
                    value={orphanSharedQuery}
                    onChange={(e) => setOrphanSharedSearch(e.target.value)}
                    aria-label="Rechercher dans Drive et Dolibarr"
                  />
                  {orphanSharedQuery ? (
                    <AppButton
                      variant="link"
                      size="sm"
                      className="position-absolute top-50 translate-middle-y text-secondary-light"
                      style={{ right: 6 }}
                      onClick={() => setOrphanSharedSearch('')}
                      title="Effacer la recherche"
                      aria-label="Effacer la recherche"
                      icon="mdi:close"
                      iconWidth={18}
                    />
                  ) : null}
                </div>
                <p className="text-sm text-secondary-light mt-1 mb-0">
                  Filtre les deux colonnes d’un coup. Vous pouvez encore affiner une seule liste avec la recherche en dessous.
                </p>
              </div>

              <div className="col-lg-6">
                <div className="client-mapping-orphan-card">
                  <div className="client-mapping-orphan-card__header">
                    <div className="client-mapping-orphan-card__eyebrow">
                      <Icon icon="mdi:folder-outline" />
                      Étape 1
                    </div>
                    <h6 className="client-mapping-orphan-card__title">
                      Dossiers sans entreprise
                      <span className="text-secondary-light fw-normal ms-2">
                        (
                        {orphanDriveQuery
                          ? `${filteredOrphanDrive.length}/${orphans.drive.length}`
                          : orphans.drive.length}
                        )
                      </span>
                    </h6>
                    <p className="client-mapping-orphan-card__hint">
                      Ce sont les dossiers clients présents dans Google Drive.
                    </p>
                    <div className="client-mapping-orphan-card__search position-relative">
                      <Icon
                        icon="mdi:magnify"
                        className="position-absolute top-50 translate-middle-y text-secondary-light"
                        style={{ left: 12, pointerEvents: 'none' }}
                      />
                      <AppInput
                        type="search"
                        className="ps-40"
                        placeholder="Rechercher un dossier…"
                        value={orphanDriveQuery}
                        onChange={(e) => setOrphanDriveQuery(e.target.value)}
                        aria-label="Rechercher un dossier"
                      />
                    </div>
                  </div>
                  <div className="client-mapping-orphan-card__body">
                    {loading ? (
                      <AppSpinner />
                    ) : orphans.drive.length === 0 ? (
                      <div className="text-center py-5 text-secondary-light px-3">
                        Tous les dossiers ont déjà une entreprise associée.
                      </div>
                    ) : filteredOrphanDrive.length === 0 ? (
                      <div className="text-center py-5 text-secondary-light">Aucun dossier trouvé</div>
                    ) : (
                      filteredOrphanDrive.map((client) => {
                        const selected = linkForm.drive_folder_id === client.folder_id;
                        return (
                          <button
                            type="button"
                            key={client.folder_id}
                            className={`client-mapping-orphan-item${selected ? ' is-selected' : ''}`}
                            onClick={() => selectDriveOrphan(client)}
                          >
                            <span className="min-w-0">
                              <span className="client-mapping-orphan-item__name d-block">{client.nom}</span>
                            </span>
                            <span className="client-mapping-orphan-item__check" aria-hidden>
                              <Icon icon="mdi:check" width={14} />
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div className="client-mapping-orphan-card">
                  <div className="client-mapping-orphan-card__header">
                    <div className="client-mapping-orphan-card__eyebrow">
                      <Icon icon="mdi:office-building-outline" />
                      Étape 2
                    </div>
                    <h6 className="client-mapping-orphan-card__title">
                      Entreprises sans dossier
                      <span className="text-secondary-light fw-normal ms-2">
                        (
                        {orphanDolibarrQuery
                          ? `${filteredOrphanDolibarr.length}/${orphans.dolibarr.length}`
                          : orphans.dolibarr.length}
                        )
                      </span>
                    </h6>
                    <p className="client-mapping-orphan-card__hint">
                      Ce sont les entreprises présentes dans Dolibarr.
                    </p>
                    <div className="client-mapping-orphan-card__search position-relative">
                      <Icon
                        icon="mdi:magnify"
                        className="position-absolute top-50 translate-middle-y text-secondary-light"
                        style={{ left: 12, pointerEvents: 'none' }}
                      />
                      <AppInput
                        type="search"
                        className="ps-40"
                        placeholder="Rechercher une entreprise…"
                        value={orphanDolibarrQuery}
                        onChange={(e) => setOrphanDolibarrQuery(e.target.value)}
                        aria-label="Rechercher une entreprise"
                      />
                    </div>
                  </div>
                  <div className="client-mapping-orphan-card__body">
                    {loading ? (
                      <AppSpinner />
                    ) : orphans.dolibarr.length === 0 ? (
                      <div className="text-center py-5 text-secondary-light px-3">
                        Toutes les entreprises ont déjà un dossier associé.
                      </div>
                    ) : filteredOrphanDolibarr.length === 0 ? (
                      <div className="text-center py-5 text-secondary-light">Aucune entreprise trouvée</div>
                    ) : (
                      filteredOrphanDolibarr.map((tp) => {
                        const selected = linkForm.dolibarr_thirdparty_id === String(tp.id);
                        return (
                          <button
                            type="button"
                            key={tp.id}
                            className={`client-mapping-orphan-item${selected ? ' is-selected' : ''}`}
                            onClick={() => selectDolibarrOrphan(tp)}
                          >
                            <span className="min-w-0">
                              <span className="client-mapping-orphan-item__name d-block">{tp.nom}</span>
                              {tp.siret ? (
                                <span className="client-mapping-orphan-item__meta d-block">
                                  SIRET {tp.siret}
                                </span>
                              ) : null}
                            </span>
                            <span className="client-mapping-orphan-item__check" aria-hidden>
                              <Icon icon="mdi:check" width={14} />
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="client-mapping-link-bar">
                  <div className="client-mapping-link-bar__pair">
                    <div className={`client-mapping-link-bar__chip${!linkForm.drive_nom ? ' is-empty' : ''}`}>
                      <Icon icon="mdi:folder-outline" className="text-secondary-light" />
                      <span className="min-w-0">
                        <span className="client-mapping-link-bar__chip-label d-block">Dossier</span>
                        <span className="client-mapping-link-bar__chip-value d-block">
                          {linkForm.drive_nom || 'À choisir à gauche'}
                        </span>
                      </span>
                    </div>
                    <Icon icon="mdi:arrow-right" className="client-mapping-link-bar__arrow" />
                    <div className={`client-mapping-link-bar__chip${!linkForm.dolibarr_nom ? ' is-empty' : ''}`}>
                      <Icon icon="mdi:office-building-outline" className="text-secondary-light" />
                      <span className="min-w-0">
                        <span className="client-mapping-link-bar__chip-label d-block">Entreprise</span>
                        <span className="client-mapping-link-bar__chip-value d-block">
                          {linkForm.dolibarr_nom || 'À choisir à droite'}
                        </span>
                      </span>
                    </div>
                  </div>
                  <AppButton
                    variant="primary"
                    className="d-inline-flex align-items-center gap-1"
                    icon="mdi:link-variant"
                    disabled={busy || !canConfirmOrphanLink}
                    loading={busy}
                    onClick={handleCreateLink}
                  >
                    Confirmer le lien
                  </AppButton>
                </div>
              </div>
            </div>
          ) : loading ? (
            <AppSpinner />
          ) : sortedRows.length === 0 ? (
            <AppEmptyState
              message={
                tab === 'suggested'
                  ? 'Aucune proposition à confirmer. Lancez la liaison automatique ou rattachez un client manuellement.'
                  : 'Aucune correspondance liée pour le moment.'
              }
            />
          ) : (
            <StickyTableResponsive>
              <table className="table table-hover mb-0 align-middle">
                <thead>
                  <tr>
                    <SortableTh sortKey="drive" label="Dossier client" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="dolibarr" label="Entreprise" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="confidence" label="Fiabilité" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh sortKey="method" label="Trouvé via" sort={sort} onSort={toggleSort} getSortIcon={getSortIcon} />
                    <SortableTh label="Actions" style={{ width: 260 }} sortable={false} />
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <div className="fw-medium">{row.drive_nom || row.nom_canonique}</div>
                      </td>
                      <td>
                        {row.dolibarr_nom ? (
                          <div className="fw-medium">{row.dolibarr_nom}</div>
                        ) : (
                          <span className="text-secondary-light">Non associé</span>
                        )}
                        {correctingId === row.id && (
                          <div className="mt-2 position-relative" style={{ maxWidth: 320 }}>
                            <AppInput
                              type="search"
                              size="sm"
                              placeholder="Rechercher la bonne entreprise…"
                              value={correctQuery}
                              autoFocus
                              onChange={(e) => {
                                setCorrectQuery(e.target.value);
                                searchDolibarr(e.target.value, setCorrectSuggestions);
                              }}
                            />
                            {correctSuggestions.length > 0 && (
                              <div className="list-group position-absolute w-100 shadow-sm" style={{ zIndex: 20 }}>
                                {correctSuggestions.map((tp) => (
                                  <button
                                    type="button"
                                    key={tp.id}
                                    className="list-group-item list-group-item-action py-1"
                                    onClick={() => applyCorrection(row.id, tp)}
                                  >
                                    {tp.nom}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td>
                        {row.match_confidence != null ? (
                          <span className="client-mapping-confidence">
                            <span className="client-mapping-confidence__bar" aria-hidden>
                              <span
                                className="client-mapping-confidence__fill"
                                style={{ width: `${Math.max(0, Math.min(100, row.match_confidence))}%` }}
                              />
                            </span>
                            {row.match_confidence}%
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        {row.match_method ? (
                          <AppBadge variant={methodBadgeVariant(row.match_method)}>
                            {METHOD_LABELS[row.match_method] || row.match_method}
                          </AppBadge>
                        ) : '—'}
                      </td>
                      <td>
                        <div className="client-mapping-actions">
                          {tab === 'suggested' && (
                            <>
                              <ActionButton
                                variant="success"
                                icon="mdi:check"
                                label="Confirmer"
                                title="Confirmer ce lien"
                                disabled={busyId === row.id}
                                onClick={() => handleValidate(row.id)}
                              />
                              <ActionButton
                                variant="primary"
                                icon="mdi:swap-horizontal"
                                label="Changer"
                                title="Choisir une autre entreprise"
                                disabled={busyId === row.id}
                                onClick={() => {
                                  setCorrectingId(row.id);
                                  setCorrectQuery('');
                                  setCorrectSuggestions([]);
                                }}
                              />
                              <ActionButton
                                variant="muted"
                                icon="mdi:close"
                                label="Ignorer"
                                title="Ignorer cette proposition"
                                disabled={busyId === row.id}
                                onClick={() => handleIgnore(row.id)}
                              />
                            </>
                          )}
                          {tab === 'validated' && (
                            <>
                              <ActionButton
                                variant="primary"
                                icon="mdi:pencil-outline"
                                label="Modifier"
                                title="Modifier l’entreprise liée"
                                disabled={busyId === row.id}
                                onClick={() => {
                                  setCorrectingId(row.id);
                                  setCorrectQuery('');
                                  setCorrectSuggestions([]);
                                }}
                              />
                              <ActionButton
                                variant="danger"
                                icon="mdi:link-variant-off"
                                label="Retirer"
                                title="Retirer ce lien"
                                disabled={busyId === row.id}
                                onClick={() => handleDelete(row.id)}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </StickyTableResponsive>
          )}
        </div>
        </>
        )}
      </div>
      {modal}
    </MasterLayout>
  );
};

export default ClientMappingAdminPage;
